import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Divider,
  Chip,
  InputAdornment,
  Stack,
  Tooltip,
  IconButton,
  FormControl,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PieChart as ChartIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Schedule as ScheduleIcon,
  Tag as TagIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import { useDebounce } from '../hooks/useDebounce';
import { FirmProfile, InvestmentThesis as InvestmentThesisType, FundSort, FundStatus, UserRole } from '../types';
import { getFunds, getGlobalThesis, createFund, getFundTheses, deleteFund, getFirmProfile, getCommonSectors, getCommonRegions } from '../services/api';
import FirmProfileView from '../components/InvestmentThesis/FirmProfileView';
import { useAuth } from '../contexts/AuthContext';
import { GlobalThesisView } from '../components/InvestmentThesis/GlobalThesisView';
import CreateFundDialog from '../components/CreateFundDialog';
import { DocumentCardSkeleton } from '../components/LoadingSkeleton';

type View = 'selection' | 'firm-profile' | 'global-thesis' | 'rules-engine' | 'version-history';

interface Fund {
  id: string;
  name: string;
  status: FundStatus;
  vintage: number;
  total_commitments: string;
  created_at: string;
  created_by: string;
}

interface FundSelectionViewProps {
  firmProfile: any;
  globalThesis: any;
  fundTheses: any[];
  funds: Fund[];
  onViewChange: (view: View) => void;
  loading?: boolean;
  onCreateFund: () => void;
  onLoadFundThesis: (fundId: string, fundName: string) => void;
  onDeleteFund: (fundId: string) => void;
  search: string;
  setSearch: (search: string) => void;
  status: FundStatus | '';
  setStatus: (status: FundStatus | '') => void;
  clearAllFilters: () => void;
  sort: FundSort;
  setSort: (sort: FundSort) => void;
  isPrivilegedUser: boolean;
  user: any;
}

const FundSelectionView: React.FC<FundSelectionViewProps & { loadingFunds?: boolean }> = ({
  firmProfile,
  globalThesis,
  fundTheses,
  funds,
  onViewChange,
  onCreateFund,
  onLoadFundThesis,
  onDeleteFund,
  loadingFunds = false,
  loading = false,
  search,
  setSearch,
  status,
  setStatus,
  clearAllFilters,
  sort,
  setSort,
  isPrivilegedUser,
  user,
}) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const hasActiveFilters = search || status;


  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Investment Thesis Management</Typography>
        <Box sx={{ minHeight: 36.5, display: 'flex', alignItems: 'center' }}>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              {isPrivilegedUser && (
                <Button
                  variant="contained"
                  onClick={() => onViewChange('global-thesis')}
                  startIcon={globalThesis ? <EditIcon /> : <AddIcon />}
                >
                  {globalThesis ? 'Edit Global Thesis' : 'Create Global Thesis'}
                </Button>
              )}
              {!isPrivilegedUser && (
                <Button
                  variant="outlined"
                  onClick={() => onViewChange('global-thesis')}
                  startIcon={<ViewIcon />}
                  disabled={!globalThesis}
                >
                  View Global Thesis
                </Button>
              )}
            </>
          )}
        </Box>
      </Paper>
      <Paper sx={{ width: '100%', height: '85vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, pb: 0 }}> {/* Added padding-bottom to separate filters from scrollable content */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📁 Fund Level Theses
                {fundTheses.length > 0 && (
                  <Chip label={`${fundTheses.length} Configured`} size="small" color="secondary" />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create specific investment theses for individual funds with customized strategies.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={onCreateFund}
              startIcon={<AddIcon />}
            >
              Create New Fund
            </Button>
          </Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search by fund name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 300,
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <Tooltip title="Toggle Filters">
                <IconButton
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  color={hasActiveFilters ? 'primary' : 'default'}
                  sx={{
                    border: hasActiveFilters ? '2px solid' : '1px solid',
                    borderColor: hasActiveFilters ? 'primary.main' : 'divider',
                  }}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem />

              <ToggleButtonGroup
                value={sort}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    const isAsc = sort.endsWith('asc');
                    setSort(isAsc ? value.replace('asc', 'desc') as FundSort : value.replace('desc', 'asc') as FundSort);
                  }
                }}
                size="small"
              >
                <ToggleButton value={FundSort.NAME_ASC}>
                  <Tooltip title="Sort by Name">
                    <TagIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={FundSort.VINTAGE_ASC}>
                  <Tooltip title="Sort by Vintage">
                    <ScheduleIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={FundSort.COMMITMENTS_ASC}>
                  <Tooltip title="Sort by Commitments">
                    <MoneyIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={FundSort.IRR_ASC}>
                  <Tooltip title="Sort by IRR">
                    <TrendingUpIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={FundSort.MOIC_ASC}>
                  <Tooltip title="Sort by MOIC">
                    <TrendingUpIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={FundSort.CREATED_ASC}>
                  <Tooltip title="Sort by Created Date">
                    <EventIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <IconButton
                onClick={() => {
                  const isAsc = sort.endsWith('asc');
                  setSort(isAsc ? sort.replace('asc', 'desc') as FundSort : sort.replace('desc', 'asc') as FundSort);
                }}
                color="primary"
              >
                <Tooltip title={`Sort ${sort.endsWith('asc') ? 'Descending' : 'Ascending'}`}>
                  <SortIcon sx={{ transform: sort.endsWith('desc') ? 'rotate(180deg)' : 'none' }} />
                </Tooltip>
              </IconButton>

              <Divider orientation="vertical" flexItem />

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, value) => value && setViewMode(value)}
                size="small"
              >
                <ToggleButton value="grid">
                  <Tooltip title="Grid View">
                    <GridViewIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list">
                  <Tooltip title="List View">
                    <ListViewIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {filtersOpen && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as FundStatus | '')}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>All Statuses</em>
                      </MenuItem>
                      <MenuItem value={FundStatus.FUNDRAISING}>Fundraising</MenuItem>
                      <MenuItem value={FundStatus.INVESTING}>Investing</MenuItem>
                      <MenuItem value={FundStatus.HARVESTING_RETURNS}>Harvesting Returns</MenuItem>
                      <MenuItem value={FundStatus.CLOSED}>Closed</MenuItem>
                    </Select>
                  </FormControl>

                  {hasActiveFilters && (
                    <Button
                      startIcon={<ClearIcon />}
                      onClick={clearAllFilters}
                      size="small"
                      color="error"
                      variant="outlined"
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, pt: 0 }}> {/* Added p:3 and pt:0 for consistent padding */}
          {/* Funds View */}
          {loadingFunds ? (
            <Grid container spacing={3}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <DocumentCardSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : Array.isArray(funds) && funds.length > 0 ? (
            viewMode === 'grid' ? (
              <Grid container spacing={3}>
                {funds.map((fund) => {
                  const hasThesis = fundTheses.some(t => t.fund_id === fund.id);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={fund.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                              {fund.name}
                            </Typography>
                            <Chip
                              label={fund.status}
                              size="small"
                              color={fund.status === 'fundraising' ? 'primary' : 'default'}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Vintage: {fund.vintage || 'N/A'}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Has Thesis</Typography>
                              <Typography variant="body2">{hasThesis ? 'Yes' : 'No'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Target Size</Typography>
                              <Typography variant="body2">${Number(fund.total_commitments).toLocaleString()}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Created</Typography>
                              <Typography variant="body2">{new Date(fund.created_at).toLocaleDateString()}</Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                          <Button
                            variant={hasThesis ? "outlined" : "contained"}
                            color="primary"
                            size="small"
                            onClick={() => onLoadFundThesis(fund.id, fund.name)}
                            startIcon={hasThesis ? <EditIcon /> : <AddIcon />}
                          >
                            {hasThesis ? 'Edit Thesis' : 'Create Thesis'}
                          </Button>
                          {user?.id === fund.created_by && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => onDeleteFund(fund.id)}
                            >
                              Delete
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Stack spacing={2}>
                {funds.map((fund) => {
                  const hasThesis = fundTheses.some(t => t.fund_id === fund.id);
                  return (
                    <Card
                      key={fund.id}
                      variant="outlined"
                      sx={{
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateX(4px)',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {fund.name}
                            </Typography>
                            <Chip
                              label={fund.status}
                              size="small"
                              color={fund.status === 'fundraising' ? 'primary' : 'default'}
                            />
                            {hasThesis && (
                              <Chip label="Has Thesis" size="small" color="success" />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Vintage: {fund.vintage || 'N/A'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '200px', flexShrink: 0 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Target Size</Typography>
                            <Typography variant="body2">${Number(fund.total_commitments).toLocaleString()}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Created</Typography>
                            <Typography variant="body2">{new Date(fund.created_at).toLocaleDateString()}</Typography>
                          </Box>
                        </Box>

                        <CardActions sx={{ flexWrap: 'nowrap', gap: 1, flexShrink: 0, width: '200px', justifyContent: 'flex-end' }}>
                          <Button
                            variant={hasThesis ? "outlined" : "contained"}
                            color="primary"
                            size="small"
                            onClick={() => onLoadFundThesis(fund.id, fund.name)}
                            startIcon={hasThesis ? <EditIcon /> : <AddIcon />}
                          >
                            {hasThesis ? 'Edit Thesis' : 'Create Thesis'}
                          </Button>
                          {user?.id === fund.created_by && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => onDeleteFund(fund.id)}
                            >
                              Delete
                            </Button>
                          )}
                        </CardActions>
                      </Box>
                    </Card>
                  );
                })}
              </Stack>
            )
          ) : (
            <Box sx={{
              textAlign: 'center',
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center', // Center items horizontally
              justifyContent: 'center'
            }}>
              <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Funds Created Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first fund to start building fund-specific investment theses.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateFund}
                sx={{ width: 'fit-content' }} // Ensure button doesn't take full width
              >
                Create New Fund
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Global Thesis Overview</DialogTitle>
        <DialogContent dividers>
          {globalThesis ? (
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 4 },
                border: 2,
                borderColor: globalThesis ? 'primary.main' : 'divider',
                opacity: 1,
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => {
                onViewChange('global-thesis');
                setInfoOpen(false);
              }}
            >
              <CardContent sx={{
                textAlign: 'center',
                py: 4,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="h3" sx={{ mb: 2 }}>🌍</Typography>
                <Typography variant="h6" gutterBottom>
                  Global Thesis
                  {globalThesis && <Chip label="✓ Configured" size="small" color="primary" sx={{ ml: 1 }} />}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Organization-wide investment strategy that applies to all funds and deals.
                </Typography>
                {!firmProfile && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    Requires firm profile
                  </Typography>
                )}
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info">No global thesis has been configured yet.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


const InvestmentThesis: React.FC = () => {
  const navigate = useNavigate();
  const { user, currentOrganization } = useAuth();
  const [view, setView] = useState<View>('selection');

  const [firmProfile, setFirmProfile] = useState<FirmProfile | null>(null);
  const [globalThesis, setGlobalThesis] = useState<InvestmentThesisType | null>(null);
  const [fundTheses, setFundTheses] = useState<any[]>([]);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [openFundDialog, setOpenFundDialog] = useState(false);
  const [deleteFundId, setDeleteFundId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [commonSectors, setCommonSectors] = useState<string[]>([]);
  const [commonRegions, setCommonRegions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [status, setStatus] = useState<FundStatus | ''>('');
  const [sort, setSort] = useState<FundSort>(FundSort.CREATED_DESC);
  
  // State for missing items dialog
  const [missingItemsDialogOpen, setMissingItemsDialogOpen] = useState(false);
  const [missingGlobalThesis, setMissingGlobalThesis] = useState(false);
  const [missingFirmProfile, setMissingFirmProfile] = useState(false);

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatus('');
  };

  const isPrivilegedUser = React.useMemo(() => {
    const role = currentOrganization?.organization_role?.toLowerCase();
    // Only Admin users can edit global thesis
    return role === UserRole.Admin.toLowerCase() || role === UserRole.Owner.toLowerCase();
  }, [currentOrganization]);

  const refetchGlobalThesis = async () => {
    try {
      const thesis = await getGlobalThesis();
      setGlobalThesis(thesis);
    } catch (error) {
      console.error('Failed to refetch global thesis:', error);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      let isMissingGlobalThesis = false;
      let isMissingFirmProfile = false;
      
      try {
        const [globalThesisResult, fundThesesResult, firmProfileResult, sectorsResult, regionsResult] = await Promise.allSettled([
          getGlobalThesis().catch(error => {
            console.error('Failed to fetch global thesis:', error);
            // Check if it's a 404 error with specific message
            if (error.response?.data?.error &&
                error.response.data.status_code === 404 &&
                error.response.data.message === "Global investment thesis not found") {
              isMissingGlobalThesis = true;
            }
            return null;
          }),
          getFundTheses().catch(error => {
            console.error('Failed to fetch fund theses:', error);
            return [];
          }),
          getFirmProfile().catch(error => {
            console.error('Failed to fetch firm profile:', error);
            // Check if it's a 404 error with specific message
            if (error.response?.data?.error &&
                error.response.data.status_code === 404 &&
                error.response.data.message === "Firm profile not found") {
              isMissingFirmProfile = true;
            }
            return null;
          }),
          getCommonSectors().catch(error => {
            console.error('Failed to fetch common sectors:', error);
            return [];
          }),
          getCommonRegions().catch(error => {
            console.error('Failed to fetch common regions:', error);
            return [];
          }),
        ]);

        if (globalThesisResult.status === 'fulfilled') {
          setGlobalThesis(globalThesisResult.value);
        }
        if (fundThesesResult.status === 'fulfilled') {
          setFundTheses(fundThesesResult.value);
        }
        if (firmProfileResult.status === 'fulfilled') {
          setFirmProfile(firmProfileResult.value);
        }
        if (sectorsResult.status === 'fulfilled') {
          setCommonSectors(sectorsResult.value);
        }
        if (regionsResult.status === 'fulfilled') {
          setCommonRegions(regionsResult.value);
        }
        // Update state variables and show dialog if needed
        setMissingGlobalThesis(isMissingGlobalThesis);
        setMissingFirmProfile(isMissingFirmProfile);
        
        if (isMissingGlobalThesis || isMissingFirmProfile) {
          setMissingItemsDialogOpen(true);
        }
      } catch (error) {
        console.error('Error loading initial investment thesis data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    const loadFunds = async () => {
      setLoadingFunds(true);
      try {
        const fundsResult = await getFunds({ search: debouncedSearchTerm || undefined, status: status || undefined, sort: sort });
        setFunds(fundsResult.funds || []);
      } catch (error) {
        console.error('Failed to fetch funds:', error);
        setFunds([]);
      } finally {
        setLoadingFunds(false);
      }
    };
    loadFunds();
  }, [debouncedSearchTerm, status, sort]);
  
  const handleViewChange = (newView: View) => {
    setView(newView);
  };

  const handleLoadFundThesis = (fundId: string) => {
    navigate(`/investment-thesis/${fundId}`);
  };

  const handleCreateFund = async (fundData: any) => {
    try {
      const newFund = await createFund(fundData);
      // Add the new fund to the state immediately
      setFunds(prevFunds => [newFund, ...prevFunds]);
      setOpenFundDialog(false);
      
      // Optionally refetch in the background to ensure consistency
      getFunds().then(data => {
        setFunds(data.funds || []);
      }).catch(error => {
        console.error('Failed to refetch funds:', error);
      });
    } catch (error) {
      console.error('Failed to create fund:', error);
    }
  };
  
  const handleDeleteFund = async () => {
    if (deleteFundId) {
      try {
        await deleteFund(deleteFundId);
        // Refetch only funds data
        const data = await getFunds();
        setFunds(data.funds || []);
        setDeleteFundId(null);
      } catch (error) {
        console.error('Failed to delete fund:', error);
      }
    }
  };

  const handleBackToSelection = () => {
    setView('selection');
  };
  
  // Missing Items Dialog handlers
  const handleCloseMissingItemsDialog = () => {
    setMissingItemsDialogOpen(false);
  };

  const handleNavigateToGlobalThesis = () => {
    setMissingItemsDialogOpen(false);
    setView('global-thesis');
  };

  const handleNavigateToFirmProfile = () => {
    setMissingItemsDialogOpen(false);
    // Navigate to AdminPanel with Firm Profile tab selected (tab index 1)
    navigate('/admin?tab=1');
  };

  const handleGlobalThesisViewChange = (newView: 'selection' | 'firm-profile' | 'global-thesis' | 'fund-thesis') => {
    if (newView !== 'fund-thesis') {
      setView(newView);
    }
  };

  if (view === 'selection') {
    return (
      <>
        <FundSelectionView
          firmProfile={firmProfile}
          globalThesis={globalThesis}
          fundTheses={fundTheses}
          funds={funds}
          onViewChange={handleViewChange}
          onCreateFund={() => setOpenFundDialog(true)}
          onLoadFundThesis={(fundId) => handleLoadFundThesis(fundId)}
          onDeleteFund={(fundId) => setDeleteFundId(fundId)}
          loading={loading}
          loadingFunds={loadingFunds}
          isPrivilegedUser={isPrivilegedUser}
          user={user}
           search={searchTerm}
           setSearch={setSearchTerm}
           status={status}
           setStatus={setStatus}
           clearAllFilters={clearAllFilters}
           sort={sort}
           setSort={setSort}
        />
        <CreateFundDialog
          open={openFundDialog}
          onClose={() => setOpenFundDialog(false)}
          onSubmit={handleCreateFund}
        />
        <Dialog open={!!deleteFundId} onClose={() => setDeleteFundId(null)}>
          <DialogTitle>Delete Fund</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this fund? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteFundId(null)}>Cancel</Button>
            <Button onClick={handleDeleteFund} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Missing Items Dialog - Include in all view states */}
        <Dialog
          open={missingItemsDialogOpen}
          onClose={handleCloseMissingItemsDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Missing Configuration</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {!isPrivilegedUser
                ? "Your workspace requires a Global Investment Thesis and/or Firm Profile to be configured. Please contact an administrator to set them up."
                : missingGlobalThesis && missingFirmProfile
                  ? "You haven't created a Global Investment Thesis and Firm Profile yet."
                  : missingGlobalThesis
                    ? "You haven't created a Global Investment Thesis yet."
                    : "You haven't created a Firm Profile yet."}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {isPrivilegedUser && "These are important components for your investment strategy."}
            </Typography>
            
            {isPrivilegedUser ? (
              <>
                {missingGlobalThesis && missingFirmProfile && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    You can create either component based on your preference.
                  </Alert>
                )}
                
                <Grid container spacing={3}>
              {/* Firm Profile Card */}
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    border: missingFirmProfile ? '1px solid' : '2px solid',
                    borderColor: missingFirmProfile ? 'divider' : 'primary.main',
                    opacity: missingFirmProfile ? 0.8 : 1,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      cursor: 'pointer'
                    }
                  }}
                  onClick={missingFirmProfile ? handleNavigateToFirmProfile : handleCloseMissingItemsDialog}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                    <Typography variant="h3" sx={{ mb: 2 }}>🏢</Typography>
                    <Typography variant="h6" gutterBottom align="center">
                      Firm Profile
                      {!missingFirmProfile && <Chip label="✓ Configured" size="small" color="success" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" paragraph>
                      Defines your firm's identity, investment focus, and operational parameters.
                    </Typography>
                    <Box sx={{ mt: 'auto', width: '100%' }}>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><MoneyIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Investment Size Range" />
                          <ListItemSecondaryAction>
                            <Chip 
                              size="small" 
                              label={missingFirmProfile ? "Not Set" : "Configured"} 
                              color={missingFirmProfile ? "default" : "success"} 
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><TagIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Sector Focus" />
                          <ListItemSecondaryAction>
                            <Chip 
                              size="small" 
                              label={missingFirmProfile ? "Not Set" : "Configured"} 
                              color={missingFirmProfile ? "default" : "success"} 
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><TrendingUpIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Exit Strategies" />
                          <ListItemSecondaryAction>
                            <Chip 
                              size="small" 
                              label={missingFirmProfile ? "Not Set" : "Configured"} 
                              color={missingFirmProfile ? "default" : "success"} 
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      </List>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
                    <Button 
                      variant={missingFirmProfile ? "contained" : "outlined"} 
                      color="primary"
                      startIcon={missingFirmProfile ? <AddIcon /> : <EditIcon />}
                      onClick={handleNavigateToFirmProfile}
                    >
                      {missingFirmProfile ? "Create Firm Profile" : "Edit Firm Profile"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              
              {/* Global Thesis Card */}
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    border: missingGlobalThesis ? '1px solid' : '2px solid',
                    borderColor: missingGlobalThesis ? 'divider' : 'primary.main',
                    opacity: missingGlobalThesis ? 0.8 : 1,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      cursor: 'pointer'
                    }
                  }}
                  onClick={missingGlobalThesis ? handleNavigateToGlobalThesis : handleCloseMissingItemsDialog}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                    <Typography variant="h3" sx={{ mb: 2 }}>🌍</Typography>
                    <Typography variant="h6" gutterBottom align="center">
                      Global Thesis
                      {!missingGlobalThesis && <Chip label="✓ Configured" size="small" color="success" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" paragraph>
                      Organization-wide investment strategy that applies to all funds and deals.
                    </Typography>
                    <Box sx={{ mt: 'auto', width: '100%' }}>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Core Investment Themes" />
                          <ListItemSecondaryAction>
                            <Chip 
                              size="small" 
                              label={missingGlobalThesis ? "Not Set" : "Configured"} 
                              color={missingGlobalThesis ? "default" : "success"} 
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><ChartIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Value Creation Levers" />
                          <ListItemSecondaryAction>
                            <Chip 
                              size="small" 
                              label={missingGlobalThesis ? "Not Set" : "Configured"} 
                              color={missingGlobalThesis ? "default" : "success"} 
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CancelIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Deal Breakers" />
                          <ListItemSecondaryAction>
                            <Chip 
                              size="small" 
                              label={missingGlobalThesis ? "Not Set" : "Configured"} 
                              color={missingGlobalThesis ? "default" : "success"} 
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      </List>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
                    <Button 
                      variant={missingGlobalThesis ? "contained" : "outlined"} 
                      color="primary"
                      startIcon={missingGlobalThesis ? <AddIcon /> : <EditIcon />}
                      onClick={handleNavigateToGlobalThesis}
                    >
                      {missingGlobalThesis ? "Create Global Thesis" : "Edit Global Thesis"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
                </Grid>
              </>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseMissingItemsDialog}
            >
              {isPrivilegedUser ? "I'll do this later" : "Close"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  if (view === 'firm-profile') {
    return <FirmProfileView onBack={handleBackToSelection} firmProfile={firmProfile} isPrivilegedUser={isPrivilegedUser} />;
  }

  if (view === 'global-thesis') {
    return <GlobalThesisView
      globalThesis={globalThesis}
      setCurrentView={handleGlobalThesisViewChange}
      isPrivilegedUser={isPrivilegedUser}
      onBack={handleBackToSelection}
      commonSectors={commonSectors}
      commonRegions={commonRegions}
      onRollback={refetchGlobalThesis}
    />;
  }


  // Return null for other views
  return null;
};

export default InvestmentThesis;
